import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FileText, Plus, Send, FileCheck2, RefreshCw, ExternalLink, Save, Eye,
  Trash2, AlertTriangle, Link2, CheckCircle2, FilePlus2, Pencil, X,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, Card, Spinner, Empty, chfFr } from "@/components/common";

const TYPE_TONE = {
  setup: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  monatsabo: "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30",
  zusatzarbeit: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  zusatzmodul: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  individuell: "bg-white/8 text-gray-300 border-white/15",
};
const STATUS_TONE = {
  entwurf: "bg-white/8 text-gray-300 border-white/15",
  bereit_fuer_bexio: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  in_bexio_erstellt: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  offen: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  bezahlt: "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30",
  "überfällig": "bg-red-500/15 text-red-300 border-red-500/30",
  storniert: "bg-white/5 text-gray-500 border-white/10",
};

const Badge = ({ tone, label, testid }) => (
  <span data-testid={testid} className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${tone || STATUS_TONE.entwurf}`}>{label}</span>
);

const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";

export default function Invoices() {
  const { t } = useI18n();
  const [config, setConfig] = useState(null);
  const [inv, setInv] = useState(null);
  const [sel, setSel] = useState(null);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/invoices");
    setInv(r.data);
    return r.data;
  }, []);

  useEffect(() => {
    load();
    api.get("/invoices/config").then((r) => setConfig(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/packages").then((r) => setPackages(r.data));
  }, [load]);

  const refresh = async (id) => {
    const data = await load();
    if (id) setSel(data.find((x) => x.id === id) || null);
  };

  if (!inv || !config) return <Spinner />;

  const total = inv.filter((i) => ["offen", "überfällig"].includes(i.status)).reduce((s, i) => s + (i.gross_chf || 0), 0);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between gap-4">
        <PageHeader title={t("invoices")} subtitle={`${t("Offen: {x}").replace("{x}", chfFr(total))} · ${config.bexio_connected ? t("bexio verbunden") : t("bexio nicht verbunden")}`} />
        <button onClick={() => setShowNew((o) => !o)} data-testid="inv-new-toggle"
          className="bg-[#A3E635] text-black font-bold rounded-xl px-4 py-2.5 flex items-center gap-2 hover:brightness-105 shrink-0">
          <Plus className="w-4 h-4" /> {t("Neue Rechnung")}
        </button>
      </div>

      {showNew && (
        <NewInvoice config={config} clients={clients} packages={packages}
          onClose={() => setShowNew(false)} onCreated={async (id) => { setShowNew(false); await refresh(id); }} />
      )}

      {/* Desktop-Tabelle */}
      <Card className="hidden md:block overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="text-[11px] uppercase tracking-wider text-gray-500">
            <th className="px-5 py-3 font-bold">{t("Nummer")}</th>
            <th className="px-5 py-3 font-bold">{t("Kunde")}</th>
            <th className="px-5 py-3 font-bold hidden md:table-cell">{t("Typ")}</th>
            <th className="px-5 py-3 font-bold hidden sm:table-cell">{t("Zeitraum")}</th>
            <th className="px-5 py-3 font-bold">{t("Total")}</th>
            <th className="px-5 py-3 font-bold">{t("Status")}</th>
            <th className="px-5 py-3 font-bold hidden lg:table-cell">bexio</th>
          </tr></thead>
          <tbody>
            {inv.map((i) => (
              <tr key={i.id} className={`border-t border-white/5 hover:bg-white/5 cursor-pointer ${sel?.id === i.id ? "bg-white/5" : ""}`} onClick={() => setSel(i)} data-testid={`inv-row-${i.id}`}>
                <td className="px-5 py-4 font-mono text-sm">{i.bexio_document_nr || i.number}</td>
                <td className="px-5 py-4 text-sm">{i.company_name}</td>
                <td className="px-5 py-4 hidden md:table-cell"><Badge tone={TYPE_TONE[i.invoice_type]} label={t(i.invoice_type_label || i.invoice_type)} /></td>
                <td className="px-5 py-4 text-sm text-gray-400 hidden sm:table-cell">{i.service_period || i.period || "—"}</td>
                <td className="px-5 py-4 font-bold">{chfFr(i.gross_chf)}</td>
                <td className="px-5 py-4"><Badge tone={STATUS_TONE[i.status]} label={t(i.status_label || i.status)} testid={`inv-status-${i.id}`} /></td>
                <td className="px-5 py-4 text-xs text-gray-400 hidden lg:table-cell">{i.bexio_document_nr ? <span className="text-[#A3E635]">✓ {i.bexio_document_nr}</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {inv.length === 0 && <Empty text={t("noData")} />}
      </Card>

      {/* Mobile-Karten */}
      <div className="md:hidden space-y-3" data-testid="inv-mobile">
        {inv.length === 0 ? <Empty text={t("noData")} /> : inv.map((i) => (
          <Card key={i.id} onClick={() => setSel(i)} data-testid={`inv-card-${i.id}`}
            className={`p-4 cursor-pointer active:scale-[0.99] transition-transform ${sel?.id === i.id ? "border-[#A3E635]/40" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-xs text-gray-500">{i.bexio_document_nr || i.number}</div>
                <div className="font-semibold text-sm truncate">{i.company_name}</div>
              </div>
              <Badge tone={STATUS_TONE[i.status]} label={t(i.status_label || i.status)} testid={`inv-card-status-${i.id}`} />
            </div>
            <div className="flex items-center justify-between gap-2 mt-3">
              <Badge tone={TYPE_TONE[i.invoice_type]} label={t(i.invoice_type_label || i.invoice_type)} />
              <div className="text-lg font-black tracking-tight">{chfFr(i.gross_chf)}</div>
            </div>
            {(i.service_period || i.period) && <div className="text-xs text-gray-500 mt-2">{i.service_period || i.period}</div>}
          </Card>
        ))}
      </div>

      {sel && <InvoiceDetail key={sel.id} invId={sel.id} initial={sel} config={config} onChanged={refresh} onClosed={() => setSel(null)} />}
    </div>
  );
}

/* ---------------- Neue Rechnung ---------------- */
function NewInvoice({ config, clients, packages, onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ client_id: "", invoice_type: "monatsabo", service_period: "", title: "", internal_note: "" });
  const [items, setItems] = useState([{ description: "", amount_chf: "" }]);
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);

  const setItem = (i, key, val) => setItems(items.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const addItem = () => setItems([...items, { description: "", amount_chf: "" }]);
  const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const applyPackage = (pid) => {
    const p = packages.find((x) => x.id === pid);
    if (!p) return;
    setItems([{ description: t("Monatsabo — Paket {name}").replace("{name}", p.name), amount_chf: p.custom ? "" : p.price_chf }]);
    setForm((f) => ({ ...f, invoice_type: "monatsabo", title: t("Monatsabo {name}").replace("{name}", p.name) }));
  };

  const lineItems = () => items.filter((it) => it.description && Number(it.amount_chf) > 0)
    .map((it) => ({ description: it.description, qty: 1, amount_chf: Number(it.amount_chf) }));
  const gross = lineItems().reduce((s, it) => s + it.amount_chf, 0);
  const net = Math.round((gross / 1.081) * 100) / 100;
  const vat = Math.round((gross - net) * 100) / 100;

  const create = async (force) => {
    if (!form.client_id) return toast.error(t("Bitte einen Kunden wählen."));
    const li = lineItems();
    if (li.length === 0) return toast.error(t("Bitte mindestens eine Position mit Betrag erfassen."));
    setBusy(true);
    try {
      if (!force) {
        const { data } = await api.get("/invoices/check-duplicate", { params: { client_id: form.client_id, service_period: form.service_period, invoice_type: form.invoice_type } });
        if (data.duplicate) { setDup(data.invoices); setBusy(false); return; }
      }
      const { data } = await api.post("/invoices", {
        client_id: form.client_id, invoice_type: form.invoice_type, amount_chf: gross,
        service_period: form.service_period, period: form.service_period, title: form.title,
        internal_note: form.internal_note, line_items: li, source_type: "manuell", force: !!force,
      });
      toast.success(t("Rechnung als Entwurf erstellt"));
      onCreated(data.id);
    } catch (e) {
      if (e?.response?.status === 409) { toast.error(e.response.data.detail); }
      else toast.error(e?.response?.data?.detail || t("Fehler beim Erstellen."));
    }
    setBusy(false);
  };

  return (
    <Card className="p-5 mb-5" data-testid="inv-new-form">
      <div className="flex items-center justify-between mb-4">
        <div className="font-bold text-lg">{t("Neue Rechnung (Entwurf)")}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="inv-new-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">{t("Kunde *")}</label>
          <select className={inp} data-testid="inv-new-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
            <option value="">{t("— wählen —")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">{t("Rechnungstyp")}</label>
          <select className={inp} data-testid="inv-new-type" value={form.invoice_type} onChange={(e) => setForm({ ...form, invoice_type: e.target.value })}>
            {Object.entries(config.types).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">{t("Leistungszeitraum")}</label>
          <input className={inp} data-testid="inv-new-period" placeholder={t("z. B. Juni 2026")} value={form.service_period} onChange={(e) => setForm({ ...form, service_period: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">{t("Aus Paket übernehmen")}</label>
          <select className={inp} data-testid="inv-new-package" onChange={(e) => applyPackage(e.target.value)} defaultValue="">
            <option value="">{t("— optional —")}</option>
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}{p.custom ? t(" (manuell)") : ` · CHF ${p.price_chf}`}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs text-gray-400 mb-1 block">{t("Positionen (Brutto, inkl. 8.1% MWST)")}</label>
        <div className="space-y-2" data-testid="inv-new-items">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input className={`${inp} flex-1`} data-testid={`inv-item-desc-${i}`} placeholder={t("Beschreibung")} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
              <input className={`${inp} w-32`} data-testid={`inv-item-amount-${i}`} type="number" placeholder="CHF" value={it.amount_chf} onChange={(e) => setItem(i, "amount_chf", e.target.value)} />
              {items.length > 1 && <button onClick={() => rmItem(i)} className="text-gray-500 hover:text-red-400 px-2" data-testid={`inv-item-rm-${i}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
        <button onClick={addItem} data-testid="inv-item-add" className="mt-2 text-xs text-[#A3E635] font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> {t("Position hinzufügen")}</button>
      </div>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div className="text-sm">
          <div className="text-gray-400">{t("Netto")} <span className="text-white font-semibold ml-1">{chfFr(net)}</span></div>
          <div className="text-gray-400">{t("MWST 8.1%")} <span className="text-white font-semibold ml-1">{chfFr(vat)}</span></div>
          <div className="text-base font-black mt-1">{t("Total")} {chfFr(gross)}</div>
        </div>
      </div>

      {dup && (
        <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="inv-dup-warning">
          <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> {t("Mögliche Doppel-Rechnung")}</div>
          <div className="text-xs text-gray-300 mb-3">{t("Für diesen Kunden / Zeitraum / Typ existiert bereits: {x}.").replace("{x}", dup.map((d) => d.number).join(", "))}</div>
          <div className="flex gap-2">
            <button onClick={() => create(true)} disabled={busy} data-testid="inv-dup-force" className="bg-amber-500 text-black font-bold rounded-lg px-3 py-2 text-sm">{t("Trotzdem erstellen")}</button>
            <button onClick={() => setDup(null)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm">{t("Abbrechen")}</button>
          </div>
        </div>
      )}

      <div className="mt-4">
        <button onClick={() => create(false)} disabled={busy} data-testid="inv-new-save" className="bg-[#A3E635] text-black font-bold rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 hover:brightness-105 disabled:opacity-50">
          <Save className="w-4 h-4" /> {t("Prüfen & als Entwurf speichern")}
        </button>
      </div>
    </Card>
  );
}

/* ---------------- Rechnungs-Detail ---------------- */
function InvoiceDetail({ invId, initial, config, onChanged, onClosed }) {
  const { t } = useI18n();
  const [inv, setInv] = useState(initial);
  const [bexioContact, setBexioContact] = useState(null);
  const [edit, setEdit] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setInv(initial); }, [initial]);
  useEffect(() => {
    if (inv?.client_id) api.get(`/clients/${inv.client_id}/bexio-contact`).then((r) => setBexioContact(r.data)).catch(() => {});
  }, [inv?.client_id, inv?.bexio_contact_id]);

  const gross = inv.gross_chf ?? 0;
  const net = inv.net_chf ?? Math.round((gross / 1.081) * 100) / 100;
  const vat = inv.vat_chf ?? Math.round((gross - net) * 100) / 100;

  const action = async (path, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/${path}`);
      setInv(data); toast.success(okMsg); onChanged(inv.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Aktion fehlgeschlagen."));
    }
    setBusy(false);
  };
  const linkContact = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${inv.client_id}/bexio-contact`);
      setBexioContact({ ...bexioContact, linked: true, bexio_contact_id: data.bexio_contact_id });
      toast.success(t("bexio-Kontakt verknüpft"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("Verknüpfung fehlgeschlagen.")); }
    setBusy(false);
  };
  const startEdit = () => {
    setEditItems((inv.line_items || []).map((li) => ({ description: li.description || li.label || "", amount_chf: li.amount_chf })));
    setEdit(true);
  };
  const saveEdit = async () => {
    const li = editItems.filter((it) => it.description && Number(it.amount_chf) > 0).map((it) => ({ description: it.description, qty: 1, amount_chf: Number(it.amount_chf) }));
    if (li.length === 0) return toast.error(t("Mindestens eine Position nötig."));
    setBusy(true);
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { line_items: li });
      setInv(data); setEdit(false); toast.success(t("Positionen gespeichert")); onChanged(inv.id);
    } catch (e) { toast.error(e?.response?.data?.detail || t("Speichern fehlgeschlagen.")); }
    setBusy(false);
  };

  const contactWarn = bexioContact && !bexioContact.linked && config.bexio_connected;
  const inBexio = !!inv.bexio_invoice_id;

  return (
    <Card className="p-6 mt-5" data-testid="inv-detail">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-xs text-gray-500">{t("Rechnung")}</div>
          <div className="text-xl font-black tracking-tight">{inv.bexio_document_nr || inv.number} · {inv.company_name}</div>
          {inv.bexio_document_nr && <div className="text-[11px] text-gray-500">{t("Interne Entwurfsnummer: {x}").replace("{x}", inv.number)}</div>}
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={TYPE_TONE[inv.invoice_type]} label={t(inv.invoice_type_label || inv.invoice_type)} />
          <Badge tone={STATUS_TONE[inv.status]} label={t(inv.status_label || inv.status)} />
          <button onClick={onClosed} className="text-gray-400 hover:text-white" data-testid="inv-detail-close"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {inv.source_type && inv.source_type !== "manuell" && (
        <div className="text-xs text-gray-400 mb-4">{t("Quelle: {x}").replace("{x}", "")}<span className="text-gray-200 font-semibold capitalize">{inv.source_type}</span>{inv.source_id ? ` · ${inv.source_id.slice(0, 8)}` : ""}</div>
      )}

      {contactWarn && (
        <div className="mb-5 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between gap-4" data-testid="inv-contact-warning">
          <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> {t("Kunde hat noch keinen verknüpften bexio-Kontakt.")}</div>
          <button onClick={linkContact} disabled={busy} data-testid="inv-link-contact" className="bg-amber-500 text-black font-bold rounded-lg px-3 py-2 text-sm flex items-center gap-2"><Link2 className="w-4 h-4" /> {t("Kontakt verknüpfen")}</button>
        </div>
      )}
      {bexioContact?.linked && (
        <div className="mb-5 text-xs text-[#A3E635] flex items-center gap-2" data-testid="inv-contact-ok"><CheckCircle2 className="w-4 h-4" /> {t("bexio-Kontakt verknüpft (ID {x})").replace("{x}", bexioContact.bexio_contact_id)}</div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5 text-sm">
        <Field label={t("Leistungszeitraum")} value={inv.service_period || inv.period} />
        <Field label={t("Rechnungsdatum")} value={inv.issue_date} />
        <Field label={t("Fälligkeit")} value={inv.due_date} />
      </div>

      {/* Positionen */}
      <div className="bg-black/30 border border-white/10 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-gray-500">{t("Positionen")}</div>
          {!edit ? (
            <button onClick={startEdit} data-testid="inv-edit-items" className="text-xs text-[#A3E635] font-semibold flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> {t("Bearbeiten")}</button>
          ) : (
            <button onClick={() => setEdit(false)} className="text-xs text-gray-400 flex items-center gap-1"><X className="w-3.5 h-3.5" /> {t("Abbrechen")}</button>
          )}
        </div>
        {!edit ? (
          <div className="space-y-1" data-testid="inv-line-items">
            {(inv.line_items || []).map((li, i) => (
              <Line key={i} label={`${li.description || li.label || t("Leistung")}${li.qty && li.qty !== 1 ? ` × ${li.qty}` : ""}`} value={chfFr(li.amount_chf)} />
            ))}
            {(!inv.line_items || inv.line_items.length === 0) && <Line label={inv.package_name || t("Leistung")} value={chfFr(gross)} />}
          </div>
        ) : (
          <div className="space-y-2" data-testid="inv-edit-form">
            {editItems.map((it, i) => (
              <div key={i} className="flex gap-2">
                <input className={`${inp} flex-1`} data-testid={`inv-edit-desc-${i}`} value={it.description} onChange={(e) => setEditItems(editItems.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
                <input className={`${inp} w-28`} type="number" data-testid={`inv-edit-amount-${i}`} value={it.amount_chf} onChange={(e) => setEditItems(editItems.map((x, idx) => idx === i ? { ...x, amount_chf: e.target.value } : x))} />
                <button onClick={() => setEditItems(editItems.filter((_, idx) => idx !== i))} className="text-gray-500 hover:text-red-400 px-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <button onClick={() => setEditItems([...editItems, { description: "", amount_chf: "" }])} className="text-xs text-[#A3E635] font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> {t("Position")}</button>
              <button onClick={saveEdit} disabled={busy} data-testid="inv-edit-save" className="ml-auto bg-[#A3E635] text-black font-bold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"><Save className="w-3.5 h-3.5" /> {t("Speichern")}</button>
            </div>
          </div>
        )}
        <div className="border-t border-white/10 mt-3 pt-2 max-w-xs ml-auto">
          <Line label={t("Nettobetrag")} value={chfFr(net)} />
          <Line label={t("darin enthalten MWST 8.1%")} value={chfFr(vat)} />
          <div className="border-t border-white/10 mt-1 pt-1"><Line label={t("Total (inkl. MWST)")} value={chfFr(gross)} big /></div>
        </div>
      </div>

      {/* bexio Meta */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5 text-sm">
        <Field label={t("Interne Entwurfsnr.")} value={inv.number} />
        <Field label={t("bexio Rechnungs-Nr. (final)")} value={inv.bexio_document_nr} />
        <Field label={t("bexio Rechnungs-ID")} value={inv.bexio_invoice_id} />
        <Field label={t("bexio Zahlungsstatus")} value={inv.bexio_payment_status} />
      </div>

      <div className="flex flex-wrap gap-3">
        <Btn icon={Eye} label={t("Vorschau (PDF)")} testid="inv-act-preview" onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")} />
        <Btn icon={Send} label={t("Für bexio vorbereiten")} testid="inv-act-prepare" disabled={busy || inBexio} onClick={() => action("prepare-bexio", t("Für bexio vorbereitet"))} />
        <Btn icon={FilePlus2} label={t("In bexio erstellen")} primary testid="inv-act-create" disabled={busy || inBexio || !config.bexio_connected} onClick={() => action("create-bexio", t("In bexio erstellt"))} />
        <Btn icon={FileCheck2} label={t("In bexio ausstellen")} testid="inv-act-issue" disabled={busy || !inBexio} onClick={() => action("issue-bexio", t("In bexio ausgestellt"))} />
        <Btn icon={RefreshCw} label={t("Status aus bexio aktualisieren")} testid="inv-act-sync" disabled={busy || !inBexio} onClick={() => action("sync-bexio", t("Status synchronisiert"))} />
      </div>
    </Card>
  );
}

const Btn = ({ icon: Ic, label, onClick, primary, testid, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${primary ? "bg-[#A3E635] text-black hover:brightness-105" : "bg-white/5 border border-white/10 hover:bg-white/10"}`}>
    <Ic className="w-4 h-4" /> {label}
  </button>
);
const Field = ({ label, value }) => (
  <div><div className="text-gray-500 text-xs">{label}</div><div className="font-medium break-all">{value || <span className="text-gray-600">—</span>}</div></div>
);
const Line = ({ label, value, big }) => (
  <div className="flex items-center justify-between py-1"><span className={big ? "font-bold" : "text-gray-400 text-sm"}>{label}</span><span className={big ? "font-black" : "font-semibold text-sm"}>{value}</span></div>
);
